import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Star, ShoppingCart, ChevronRight, Shield, Truck, RotateCcw, Store, Share2 } from 'lucide-react';
import { useMarketplace } from '../context/MarketplaceContext';
import { getProductById, getMerchantById, PRODUCTS, CATEGORIES } from '../data/marketplaceData';
import ProductCard from '../components/marketplace/ProductCard';
import CartDrawer from '../components/marketplace/CartDrawer';
import CheckoutDrawer from '../components/marketplace/CheckoutDrawer';

export default function ProductDetailPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const {
    addToCart,
    cart,
    showCart,
    setShowCart,
    showCheckout,
    trackProductView,
  } = useMarketplace();

  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAdding, setIsAdding] = useState(false);

  const product = getProductById(productId);
  const merchant = product ? getMerchantById(product.merchantId) : null;

  // Track view when product loads
  useEffect(() => {
    if (product) {
      trackProductView(product.id);
      setQuantity(1);
      setCurrentImageIndex(0);
    }
  }, [product, trackProductView]);

  // Get related products (same category or complementary)
  const relatedProducts = product
    ? PRODUCTS.filter(
        (p) =>
          p.id !== product.id &&
          (p.category === product.category ||
            p.tags?.some((tag) => product.tags?.includes(tag)))
      ).slice(0, 8)
    : [];

  const handleBack = () => {
    navigate('/marketplace');
  };

  const handleAddToCart = () => {
    setIsAdding(true);
    addToCart(product.id, quantity);
    setTimeout(() => {
      setIsAdding(false);
    }, 600);
  };

  const handleQuantityChange = (delta) => {
    setQuantity((prev) => Math.max(1, Math.min(prev + delta, product.stock)));
  };

  const handleImageNav = (direction) => {
    setCurrentImageIndex((prev) => {
      const total = product.images.length;
      return direction === 'next' ? (prev + 1) % total : (prev - 1 + total) % total;
    });
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: product.name,
          text: `Check out ${product.name} on Bonndle Marketplace!`,
          url,
        });
      } catch (err) {
        // User cancelled
      }
    } else {
      await navigator.clipboard.writeText(url);
      alert('Link copied to clipboard!');
    }
  };

  // Calculate cart item count for badge
  const cartItemCount = cart.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Product not found</p>
          <button
            onClick={handleBack}
            className="text-indigo-600 font-bold hover:underline"
          >
            Back to Marketplace
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="flex items-center justify-between px-4 h-14">
          <button
            onClick={handleBack}
            className="p-2 -ml-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full"
          >
            <ChevronLeft size={24} />
          </button>
          <h1 className="font-bold text-gray-900 truncate flex-1 text-center mx-4">
            Product Details
          </h1>
          <div className="flex items-center gap-2">
            <button
              onClick={handleShare}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full"
            >
              <Share2 size={20} />
            </button>
            <button
              onClick={() => setShowCart(true)}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full relative"
            >
              <ShoppingCart size={20} />
              {cartItemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-indigo-600 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {cartItemCount > 99 ? '99+' : cartItemCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="pb-24">
        {/* Image Gallery */}
        <div className="relative aspect-square bg-gray-100">
          <img
            src={product.images[currentImageIndex]}
            alt={product.name}
            className="w-full h-full object-cover"
          />

          {/* Image Navigation */}
          {product.images.length > 1 && (
            <>
              <button
                onClick={() => handleImageNav('prev')}
                className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 rounded-full shadow-lg hover:bg-white"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={() => handleImageNav('next')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 rounded-full shadow-lg hover:bg-white"
              >
                <ChevronRight size={20} />
              </button>

              {/* Dots */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {product.images.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      idx === currentImageIndex ? 'bg-indigo-600 w-4' : 'bg-white/70'
                    }`}
                  />
                ))}
              </div>
            </>
          )}

          {/* Discount Badge */}
          {product.discount > 0 && (
            <div className="absolute top-4 left-4 bg-red-500 text-white text-sm font-bold px-3 py-1.5 rounded-full shadow-lg">
              -{product.discount}% OFF
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="p-4 space-y-4 bg-white">
          {/* Merchant */}
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
              <Store size={16} className="text-indigo-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Sold by</p>
              <p className="text-sm font-bold text-indigo-600">{merchant?.name}</p>
            </div>
            {merchant?.verified && (
              <span className="ml-auto bg-emerald-100 text-emerald-700 text-[10px] font-bold px-2 py-0.5 rounded-full">
                Verified
              </span>
            )}
          </div>

          {/* Title & Rating */}
          <div>
            <h1 className="text-xl font-black text-gray-900 leading-tight">
              {product.name}
            </h1>
            <div className="flex items-center gap-2 mt-2">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    size={16}
                    className={
                      star <= Math.round(product.rating)
                        ? 'text-amber-400 fill-amber-400'
                        : 'text-gray-200'
                    }
                  />
                ))}
              </div>
              <span className="text-sm font-bold text-gray-700">{product.rating}</span>
              <span className="text-sm text-gray-400">({product.reviewCount} reviews)</span>
            </div>
          </div>

          {/* Price Row with Add to Cart */}
          <div className="flex items-center justify-between">
            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-black text-gray-900">
                ${product.price.toFixed(2)}
              </span>
              {product.originalPrice > product.price && (
                <>
                  <span className="text-lg text-gray-400 line-through">
                    ${product.originalPrice.toFixed(2)}
                  </span>
                  <span className="text-sm font-bold text-emerald-600">
                    Save ${(product.originalPrice - product.price).toFixed(2)}
                  </span>
                </>
              )}
            </div>
            {/* Add to Cart Button - Right side of pricing */}
            <button
              onClick={handleAddToCart}
              disabled={product.stock === 0 || isAdding}
              className={`px-4 py-2.5 rounded-xl font-bold text-white flex items-center gap-2 transition-all ${
                isAdding
                  ? 'bg-green-500'
                  : product.stock === 0
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98]'
              }`}
            >
              <ShoppingCart size={18} className={isAdding ? 'animate-bounce' : ''} />
              <span>{isAdding ? 'Added!' : 'Add'}</span>
            </button>
          </div>

          {/* Stock */}
          <div className="flex items-center gap-2">
            {product.stock > 10 ? (
              <span className="text-sm text-emerald-600 font-bold">In Stock</span>
            ) : product.stock > 0 ? (
              <span className="text-sm text-amber-600 font-bold">
                Only {product.stock} left
              </span>
            ) : (
              <span className="text-sm text-red-600 font-bold">Out of Stock</span>
            )}
            <span className="text-sm text-gray-400">|</span>
            <span className="text-sm text-gray-500">
              {product.sales.toLocaleString()}+ sold
            </span>
          </div>

          {/* Description */}
          <div className="pt-2 border-t border-gray-100">
            <h3 className="font-bold text-gray-900 mb-2">Description</h3>
            <p className="text-gray-600 text-sm leading-relaxed">{product.description}</p>
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-2">
            {product.tags.map((tag) => (
              <span
                key={tag}
                className="bg-gray-100 text-gray-600 text-xs font-medium px-2.5 py-1 rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>

          {/* Trust Badges */}
          <div className="grid grid-cols-3 gap-3 py-4 border-t border-gray-100">
            <div className="text-center">
              <div className="w-10 h-10 mx-auto bg-indigo-100 rounded-full flex items-center justify-center">
                <Shield size={18} className="text-indigo-600" />
              </div>
              <p className="text-[10px] font-bold text-gray-600 mt-1">Secure Payment</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 mx-auto bg-indigo-100 rounded-full flex items-center justify-center">
                <Truck size={18} className="text-indigo-600" />
              </div>
              <p className="text-[10px] font-bold text-gray-600 mt-1">Fast Shipping</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 mx-auto bg-indigo-100 rounded-full flex items-center justify-center">
                <RotateCcw size={18} className="text-indigo-600" />
              </div>
              <p className="text-[10px] font-bold text-gray-600 mt-1">Easy Returns</p>
            </div>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-3 p-4 bg-white">
            <h3 className="font-bold text-gray-900 mb-3">You May Also Like</h3>
            <div className="grid grid-cols-2 gap-3">
              {relatedProducts.slice(0, 4).map((rec) => (
                <ProductCard key={rec.id} product={rec} compact />
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Fixed Bottom Bar - Quantity + Add to Cart */}
      <div className="fixed bottom-0 left-0 right-0 border-t border-gray-100 p-4 bg-white z-40">
        <div className="flex items-center gap-4 max-w-lg mx-auto">
          {/* Quantity Selector */}
          <div className="flex items-center gap-2 bg-gray-100 rounded-xl p-1">
            <button
              onClick={() => handleQuantityChange(-1)}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              disabled={quantity <= 1}
            >
              <span className={`text-lg font-bold ${quantity <= 1 ? 'text-gray-300' : 'text-gray-600'}`}>
                −
              </span>
            </button>
            <span className="w-8 text-center font-bold text-gray-900">{quantity}</span>
            <button
              onClick={() => handleQuantityChange(1)}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              disabled={quantity >= product.stock}
            >
              <span className={`text-lg font-bold ${quantity >= product.stock ? 'text-gray-300' : 'text-gray-600'}`}>
                +
              </span>
            </button>
          </div>

          {/* Add to Cart Button */}
          <button
            onClick={handleAddToCart}
            disabled={product.stock === 0 || isAdding}
            className={`flex-1 py-3 px-6 rounded-xl font-bold text-white flex items-center justify-center gap-2 transition-all ${
              isAdding
                ? 'bg-green-500'
                : product.stock === 0
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98]'
            }`}
          >
            <ShoppingCart size={20} className={isAdding ? 'animate-bounce' : ''} />
            <span>
              {isAdding ? 'Added!' : `Add to Cart - $${(product.price * quantity).toFixed(2)}`}
            </span>
          </button>
        </div>
      </div>

      {/* Cart Drawer */}
      <CartDrawer />

      {/* Checkout Drawer */}
      <CheckoutDrawer />
    </div>
  );
}
